import React from 'react';
import { AxiosError } from 'axios';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { IApiConfig, useApi } from '../../../../hooks/useApi';
import { useIntervalWithLimit } from '../../../../hooks/useIntervalWithLimit';
import { useUser } from '../../../../hooks/useUser';
import {
    ADD_FAILURE_REASONS,
    AddAttempt,
    AddFailureReason,
    AddIdentityProviderInput,
    AddIdentityProviderRequest,
    ProvisioningState,
    ProvisioningStatusResponse,
} from '../identityProviderManagement.types';
import { isInFlight, nameKey } from '../identityProviderRows';

/**
 * Polling cadence: 90 attempts at 2.5s, roughly 225 seconds.
 *
 * This MUST stay bounded. UasMigrationStatusContext polls with unbounded
 * setTimeout recursion and no escape hatch; copying it would leave an admin on a
 * spinner forever if provisioning stalls.
 *
 * The cap is a safety net, not the mechanism: the authoritative timeout is
 * server-side. This must sit comfortably above it so the server's verdict always
 * arrives first and the client never invents a timeout the server disagrees
 * with. It fires only when the poll endpoint itself is unreachable.
 *
 * The server waits up to ~150s for the auth broker to settle and then up to ~25s
 * more to resolve the created provider, so its verdict can take ~175s. 60
 * attempts was sized against an earlier 90s broker wait and would now fire
 * first, which is the inversion this comment exists to prevent.
 */
const POLL_INTERVAL_MS = 2500;
const POLL_ATTEMPT_LIMIT = 90;

type Action =
    | { type: 'submit'; key: string; input: AddIdentityProviderInput }
    | { type: 'accept'; key: string; provisioningId: string }
    | { type: 'reject'; key: string; reason?: AddFailureReason }
    | { type: 'poll'; key: string; status: ProvisioningStatusResponse }
    | { type: 'stopWaiting'; key: string }
    | { type: 'remove'; keys: string[] };

/** The state an add attempt is in after a poll returns `status`. */
const stateAfterPoll = ({
    status,
    failureKind,
}: ProvisioningStatusResponse): ProvisioningState => {
    const failed =
        failureKind === 'TIMED_OUT'
            ? ProvisioningState.TIMED_OUT
            : ProvisioningState.FAILED;
    return {
        SUBMITTED: ProvisioningState.SUBMITTED,
        PROVISIONING: ProvisioningState.PROVISIONING,
        PROVISIONED: ProvisioningState.PROVISIONED,
        FAILED: failed,
    }[status];
};

/**
 * Whether a new add attempt of `input` replaces `attempt` in the list.
 * Replaced: a submit the server rejected (it has no row), and a failed or
 * timed-out add attempt of the same name (the new one is its retry).
 */
const isReplacedBy = (
    attempt: AddAttempt,
    input: AddIdentityProviderInput,
): boolean =>
    attempt.state === ProvisioningState.SUBMIT_REJECTED ||
    ((attempt.state === ProvisioningState.FAILED ||
        attempt.state === ProvisioningState.TIMED_OUT) &&
        nameKey(attempt.input.displayName) === nameKey(input.displayName));

const update = (
    attempts: AddAttempt[],
    key: string,
    change: Partial<AddAttempt>,
): AddAttempt[] => attempts.map(a => (a.key === key ? { ...a, ...change } : a));

/** All add attempts made on this page, newest first. Exported for tests. */
export const addAttemptsReducer = (
    attempts: AddAttempt[],
    action: Action,
): AddAttempt[] => {
    switch (action.type) {
        case 'submit':
            return [
                {
                    key: action.key,
                    input: action.input,
                    state: ProvisioningState.SUBMITTING,
                },
                ...attempts.filter(a => !isReplacedBy(a, action.input)),
            ];
        case 'accept':
            return update(attempts, action.key, {
                state: ProvisioningState.SUBMITTED,
                provisioningId: action.provisioningId,
            });
        case 'reject':
            return update(attempts, action.key, {
                state: ProvisioningState.SUBMIT_REJECTED,
                rejectedReason: action.reason,
            });
        case 'poll':
            return update(attempts, action.key, {
                state: stateAfterPoll(action.status),
                identityProviderId: action.status.identityProviderId,
                connectionName: action.status.connectionName,
                failureKind: action.status.failureKind,
            });
        case 'stopWaiting':
            return update(attempts, action.key, {
                state: ProvisioningState.TIMED_OUT,
            });
        case 'remove':
            return attempts.filter(a => !action.keys.includes(a.key));
        default:
            return attempts;
    }
};

export interface UseAddIdentityProviderResult {
    /** All add attempts made on this page, newest first. */
    attempts: AddAttempt[];
    /** The add attempt not finished yet, if any. There is at most one. */
    inFlight?: AddAttempt;
    /**
     * Start a new add attempt and return its key. Call only when nothing is in
     * flight. The outcome arrives in `attempts`.
     */
    submit: (input: AddIdentityProviderInput) => string;
    /** Forget add attempts the list no longer needs. */
    remove: (keys: string[]) => void;
    isPolling: boolean;
}

interface Options {
    /** Called once when an add attempt is provisioned. */
    onProvisioned?: () => void;
}

/**
 * Adds identity providers and tracks each add attempt until the page is left.
 *
 * Each add attempt is a record in `attempts`. The one in flight is submitted,
 * then polled until it is provisioned, fails or times out. Records stay after
 * that, so the list can keep showing the outcome; the caller removes them when
 * the provider list includes the new provider.
 */
export const useAddIdentityProvider = ({
    onProvisioned,
}: Options = {}): UseAddIdentityProviderResult => {
    const { api } = useAppConfigContext();
    const user = useUser();
    const baseUrl = `${api?.authProv}/v1/data/tenants/${user?.tenantIdentifier}/identityProviders`;

    const [attempts, dispatch] = React.useReducer(addAttemptsReducer, []);
    const inFlight = attempts.find(isInFlight);
    /** The add attempt being polled: in flight and accepted by the server. */
    const polled = inFlight?.provisioningId ? inFlight : undefined;

    const nextKey = React.useRef(1);
    const submittedKey = React.useRef<string>();
    const polledKey = React.useRef<string>();
    polledKey.current = polled?.key;
    const onProvisionedRef = React.useRef(onProvisioned);
    onProvisionedRef.current = onProvisioned;

    const submitConfig = React.useMemo<IApiConfig>(
        () => ({
            url: baseUrl,
            method: 'POST',
            useAuth: true,
            executeType: 'manual',
            useCache: false,
        }),
        [baseUrl],
    );
    const {
        data: submitData,
        error: submitError,
        execute: executeSubmit,
    } = useApi<{ provisioningId: string }>(submitConfig);

    const pollConfig = React.useMemo<IApiConfig>(
        () => ({
            url: `${baseUrl}/provisioning/${polled?.provisioningId}`,
            method: 'GET',
            useAuth: true,
            executeType: 'manual',
            useCache: false,
        }),
        [baseUrl, polled?.provisioningId],
    );
    const { data: pollData, execute: executePoll } =
        useApi<ProvisioningStatusResponse>(pollConfig);

    const poller = useIntervalWithLimit(
        executePoll,
        POLL_INTERVAL_MS,
        POLL_ATTEMPT_LIMIT,
        !polled,
    );
    const pollerRef = React.useRef(poller);
    pollerRef.current = poller;

    // The server accepted the add attempt: start polling it.
    React.useEffect(() => {
        if (!submitData?.provisioningId || !submittedKey.current) return;
        dispatch({
            type: 'accept',
            key: submittedKey.current,
            provisioningId: submitData.provisioningId,
        });
        pollerRef.current.resetCounter();
        pollerRef.current.enable();
    }, [submitData]);

    // The server rejected the add attempt. Keep the reason only if it is a
    // known one.
    React.useEffect(() => {
        if (!submitError || !submittedKey.current) return;
        const details = (submitError as AxiosError<{ details?: unknown }>)
            ?.response?.data?.details;
        dispatch({
            type: 'reject',
            key: submittedKey.current,
            reason: ADD_FAILURE_REASONS.find(reason => reason === details),
        });
    }, [submitError]);

    // A poll returned.
    React.useEffect(() => {
        if (!pollData || !polledKey.current) return;
        dispatch({ type: 'poll', key: polledKey.current, status: pollData });
        if (pollData.status === 'PROVISIONED') onProvisionedRef.current?.();
    }, [pollData]);

    // Polling hit its cap without an answer.
    React.useEffect(() => {
        if (poller.isStopped && polledKey.current) {
            dispatch({ type: 'stopWaiting', key: polledKey.current });
        }
    }, [poller.isStopped]);

    // Nothing left to poll.
    React.useEffect(() => {
        if (!polled) pollerRef.current.disable();
    }, [polled]);

    const submit = React.useCallback(
        (input: AddIdentityProviderInput) => {
            const key = `add-${nextKey.current}`;
            nextKey.current += 1;
            submittedKey.current = key;
            dispatch({ type: 'submit', key, input });

            // The type picks setup guidance only and is not sent.
            const data: AddIdentityProviderRequest = {
                displayName: input.displayName,
                metadataUrl: input.metadataUrl,
                mapping: { user_id: input.nameIdentifier },
            };
            executeSubmit({ data });
            return key;
        },
        [executeSubmit],
    );

    const remove = React.useCallback(
        (keys: string[]) => dispatch({ type: 'remove', keys }),
        [],
    );

    return {
        attempts,
        inFlight,
        submit,
        remove,
        isPolling: poller.isRunning,
    };
};
